import React from 'react';
import { ArrowRight, Smartphone } from 'lucide-react';
import { MODULES } from './ModuleShowcase';
import SCREENS from '../data/screens';
import ANDROID_SCREENS from '../data/androidScreens';
import { MODULE_SCREENS } from './ModuleShowcase';

/**
 * The index for the module reference.
 *
 * The manual is long and the detailed module section sits several screens
 * down, so on opening the page a reader saw no interface at all. This grid
 * puts every screen on the first fold and jumps to the full entry.
 *
 * These were dark .card--media tiles with the screenshot squeezed into a
 * third of the card — at that size a screenshot is a grey smudge, and a
 * black tile is a strange thing to put a screenshot of a white app on. The
 * card is light now and the shot takes the full width at the top, cropped
 * to its own header, which is the part that identifies a screen. The
 * capture count rides on the image, because "12 screens" is the promise
 * that makes the tile worth clicking.
 */
const shotsFor = id => (MODULE_SCREENS[id] || []).flatMap(slug => SCREENS[slug] || []);

/* Not every module has a hand-picked hero image — the ones added later are
   documented from their captures alone. Fall back to the module's first
   capture, and if it has neither, the card drops its figure rather than
   rendering a broken one. */
const figureFor = module => module.shot || shotsFor(module.id)[0]?.src || null;

const androidCount = Object.values(ANDROID_SCREENS).reduce((n, v) => n + v.length, 0);

const ModuleGallery = () => (
  <section className="mb-12">
    <h2 className="text-2xl font-bold text-content">The product at a glance</h2>
    <p className="mb-6 mt-2 leading-relaxed text-content-muted">
      Every screen in WASLX, with what it does and what you can do there. Pick one to jump straight
      to it, or read through from{' '}
      <a href="#modules" className="font-bold text-primary-strong underline-offset-2 hover:underline">
        the module reference
      </a>
      .
    </p>

    {/* The web dashboard is only half the product. Without this the index
        reads as "WASLX is a web app", and the Android reference three
        screens down is never found by anyone scanning the top. */}
    <a href="#phone-agents" className="accent-4 focus-ring android-pointer">
      <span className="android-pointer__icon">
        <Smartphone className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="android-pointer__title">…and the Android app</span>
        <span className="android-pointer__sub">
          {androidCount} screens captured on a real handset — what agents actually work in
        </span>
      </span>
      <ArrowRight className="android-pointer__go" />
    </a>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {MODULES.map((module, i) => {
        const Icon = module.icon;
        const shots = shotsFor(module.id);
        const count = shots.length;
        const figure = figureFor(module);
        return (
          <a
            key={module.id}
            href={`#${module.id}`}
            className={`${module.accent} focus-ring glance-card`}
          >
            {figure && (
              <span className="glance-card__figure">
                <img
                  src={figure}
                  alt=""
                  loading={i < 3 ? 'eager' : 'lazy'}
                  width="1400"
                  height="840"
                />
                {count > 0 && (
                  <span className="glance-card__count">
                    {count} {count === 1 ? 'screen' : 'screens'}
                  </span>
                )}
              </span>
            )}

            <span className="glance-card__body">
              <span className="glance-card__icon">
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="glance-card__title">{module.name}</span>
                <span className="glance-card__who">{module.who}</span>
              </span>
              <ArrowRight className="glance-card__go" />
            </span>
          </a>
        );
      })}
    </div>
  </section>
);

export default ModuleGallery;
